// Célébration affichée quand une catégorie vient d'être terminée
// (même esprit visuel que la célébration de fin d'unité du sentier).
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Picto } from './common/Picto';
import { displayCategoryLabel } from '../utils/categoryLabels';
import './CategoryCelebration.css';

const AUTO_CLOSE_MS = 3500;

const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  id: i,
  angle: (i / 16) * 360 + 11,
  dist: 90 + (i % 4) * 26,
  delay: 0.25 + i * 0.04,
  size: 8 + (i % 3) * 4,
  shape: i % 3, // 0 losange, 1 rond, 2 étoile
  color: ['var(--color-accent, #C87040)', '#D4784A', '#D9A441', 'var(--gold-dark, #A07830)'][i % 4],
}));

const Particle: React.FC<{ shape: number; size: number }> = ({ shape, size }) => {
  if (shape === 0) return (
    <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true">
      <polygon points="5,0 10,5 5,10 0,5" fill="currentColor" />
    </svg>
  );
  if (shape === 1) return (
    <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true">
      <circle cx="5" cy="5" r="4" fill="currentColor" />
    </svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 10 10" aria-hidden="true">
      <polygon points="5,0 6.2,3.8 10,5 6.2,6.2 5,10 3.8,6.2 0,5 3.8,3.8" fill="currentColor" />
    </svg>
  );
};

interface CategoryCelebrationProps {
  category: string;
  onClose: () => void;
}

const CategoryCelebration: React.FC<CategoryCelebrationProps> = ({ category, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const id = window.setTimeout(onClose, AUTO_CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [onClose]);

  return (
    <motion.div
      className="ccelebration-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      role="dialog"
      aria-live="polite"
    >
      <motion.div
        className="ccelebration-card"
        initial={{ scale: 0.6, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
        onClick={e => e.stopPropagation()}
      >
        {PARTICLES.map(p => {
          const rad = (p.angle * Math.PI) / 180;
          return (
            <motion.div
              key={p.id}
              className="ccelebration-particle"
              initial={{ opacity: 0, x: 0, y: 0, scale: 0, rotate: 0 }}
              animate={{
                opacity: [0, 1, 1, 0],
                x: Math.cos(rad) * p.dist,
                y: Math.sin(rad) * p.dist,
                scale: [0, 1.3, 1, 0.6],
                rotate: p.id % 2 === 0 ? 200 : -200,
              }}
              transition={{ duration: 1.5, delay: p.delay, ease: 'easeOut' }}
              style={{ color: p.color }}
            >
              <Particle shape={p.shape} size={p.size} />
            </motion.div>
          );
        })}

        <motion.div
          className="ccelebration-icon"
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: [0, 1.15, 1], rotate: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
        >
          <Picto name="trophee" size={72} />
        </motion.div>

        <motion.p
          className="ccelebration-kicker"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {t('categoryCelebration.kicker')}
        </motion.p>
        <motion.h2
          className="ccelebration-title"
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {displayCategoryLabel(category, t)}
        </motion.h2>
        <motion.p
          className="ccelebration-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {t('categoryCelebration.subtitle')}
        </motion.p>

        <motion.button
          className="ccelebration-btn"
          onClick={onClose}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          whileTap={{ scale: 0.96 }}
        >
          {t('categorySelection.continueArrow')}
        </motion.button>

        {/* Barre de fermeture automatique */}
        <motion.div
          className="ccelebration-timer"
          initial={{ scaleX: 1 }}
          animate={{ scaleX: 0 }}
          transition={{ duration: AUTO_CLOSE_MS / 1000, ease: 'linear' }}
        />
      </motion.div>
    </motion.div>
  );
};

export default CategoryCelebration;
